<template>
    <div class="container-nzidoo">
        <page-title title="sidebar.boosterPlansManagement" />

        <!-- Toolbar -->
        <div class="admin-toolbar">
            <div class="admin-search-box">
                <i class="mdi mdi-magnify admin-search-icon"></i>

                <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')"
                    class="admin-search-input">

                <!-- Clear icon -->
                <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
                    :title="$t('sentence.clear')"></i>
            </div>

            <button class="btn btn-primary" @click="openCreateModal">
                <i class="mdi mdi-plus"></i>
                <div class="d-none d-md-block">
                    {{ $t("action.add") }}
                </div>
            </button>
        </div>

        <LoadingSpinner v-if="loader" />
        <!-- Booster Plans List -->
        <div v-if="filteredPlans && filteredPlans.length" class="card-body p-0">
            <div class="table-responsive">
                <table class="table table-hover mb-0">
                    <thead class="thead-light">
                        <tr>
                            <th class="border-top-0">{{ $t('attr.sortOrder') }}</th>
                            <th class="border-top-0">{{ $t('attr.name') }}</th>
                            <th class="border-top-0 d-none d-md-table-cell">{{ $t('attr.description') }}</th>
                            <th class="border-top-0">{{ $t('attr.duration') }}</th>
                            <th class="border-top-0">{{ $t('attr.price') }}</th>
                            <th class="border-top-0 d-none d-lg-table-cell">{{ $t('general.type') }}</th>
                            <th class="border-top-0">{{ $t('main.status') }}</th>
                            <th class="border-top-0">{{ $t('main.action') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="plan in filteredPlans" :key="plan.id">
                            <td>
                                <span class="font-weight-bold text-primary">#{{ plan.sort_order }}</span>
                            </td>
                            <td>
                                <span class="font-weight-semibold">{{ plan.name }}</span>
                            </td>
                            <td class="d-none d-md-table-cell">
                                <span class="text-truncate d-inline-block" style="max-width: 300px;">
                                    {{ plan.description }}
                                </span>
                            </td>
                            <td>
                                <span class="text-muted">{{ plan.duration_days }} {{ $t('attr.days') }}</span>
                            </td>
                            <td>
                                <span class="font-weight-bold text-success">{{ formatPrice(plan.price) }}</span>
                            </td>
                            <td class="d-none d-lg-table-cell">
                                <span class="text-capitalize">{{ $t(`boosterPlans.type.${plan.type}`) }}</span>
                            </td>
                            <td>
                                <span
                                    :class="['badge', 'badge-pill', plan.is_active ? 'badge-success' : 'badge-secondary']">
                                    {{ plan.is_active ? $t('status.active') : $t('status.inactive') }}
                                </span>
                            </td>
                            <td>
                                <div class="d-flex gap-2">
                                    <button @click="edit(plan.id)"
                                        class="btn btn-sm btn-warning rounded-circle admin-action-btn"
                                        :title="$t('action.edit')">
                                        <i class="mdi mdi-pencil"></i>
                                    </button>
                                    <button @click="remove(plan.id)"
                                        class="btn btn-sm btn-danger rounded-circle admin-action-btn"
                                        :title="$t('action.delete')">
                                        <i class="mdi mdi-delete"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <!-- Empty State -->
            <div v-if="filteredPlans.length === 0" class="card-body text-center py-5">
                <svg class="admin-empty-icon mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="2" style="width: 64px; height: 64px; margin: 0 auto; color: #cbd5e0;">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p class="text-muted">{{ $t('sentence.noResults') }}</p>
            </div>

            <!-- Empty State -->
            <div v-if="filteredPlans.length === 0" class="admin-empty-state">
                <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p class="admin-empty-text">{{ $t('sentence.noResults') }}</p>
            </div>
        </div>

        <!-- Drawer Backdrop -->
        <transition name="drawer-backdrop">
            <div v-if="showModal" class="admin-drawer-backdrop" @click="closeModal"></div>
        </transition>

        <!-- Drawer -->
        <transition name="drawer">
            <div v-if="showModal" class="admin-drawer-container">
                <div class="admin-drawer-header">
                    <h3 class="admin-drawer-title">
                        {{ isEditing ? $t('boosterPlans.editPlan') : $t('boosterPlans.addPlan') }}
                    </h3>
                    <button class="btn btn-close" @click="closeModal">
                        <i class="mdi mdi-close"></i>
                    </button>
                </div>

                <div class="admin-drawer-body">
                    <div class="admin-form-group">
                        <label class="admin-form-label">{{ $t('attr.name') }} *</label>
                        <input type="text" v-model="form.name" class="admin-form-input" :placeholder="$t('attr.name')">
                    </div>

                    <div class="admin-form-group">
                        <label class="admin-form-label">{{ $t('attr.description') }} *</label>
                        <textarea v-model="form.description" class="admin-form-textarea" rows="4"
                            :placeholder="$t('attr.description')"></textarea>
                    </div>

                    <div class="form-row">
                        <div class="admin-form-group">
                            <label class="admin-form-label">{{ $t('attr.duration') }} *</label>
                            <input type="number" v-model.number="form.duration_days" class="admin-form-input" min="1"
                                :placeholder="$t('attr.duration')">
                        </div>

                        <div class="admin-form-group">
                            <label class="admin-form-label">{{ $t('attr.price') }} *</label>
                            <input type="number" v-model.number="form.price" class="admin-form-input" min="0"
                                step="0.01" :placeholder="$t('attr.price')">
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="admin-form-group">
                            <label class="admin-form-label">{{ $t('general.type') }} *</label>
                            <select v-model="form.type" class="admin-form-input">
                                <option value="">{{ $t('action.selecteOne') }}</option>
                                <option value="boost_ad">{{ $t('boosterPlans.type.boost_ad') }}</option>
                                <option value="top_visibility">{{ $t('boosterPlans.type.top_visibility') }}</option>
                                <option value="urgent_ad">{{ $t('boosterPlans.type.urgent_ad') }}</option>
                            </select>
                        </div>

                        <div class="admin-form-group">
                            <label class="admin-form-label">{{ $t('attr.sortOrder') }}</label>
                            <input type="number" v-model.number="form.sort_order" class="admin-form-input" min="0"
                                :placeholder="$t('attr.sortOrder')">
                            <span class="admin-form-hint">{{ $t('attr.sortOrder') }}</span>
                        </div>
                    </div>

                    <div class="admin-form-group">
                        <label class="admin-form-label">{{ $t('main.status') }}</label>
                        <div class="toggle-container">
                            <label class="toggle-switch">
                                <input type="checkbox" v-model="form.is_active">
                                <span class="toggle-slider"></span>
                            </label>
                            <span class="toggle-label">
                                {{ form.is_active ? $t('status.active') : $t('status.inactive') }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="admin-drawer-footer">
                    <button class="btn btn-secondary" @click="closeModal">
                        {{ $t('action.cancel') }}
                    </button>
                    <button class="btn btn-primary" @click="save" :disabled="!isFormValid">
                        {{ isEditing ? $t('action.update') : $t('action.create') }}
                    </button>
                </div>
            </div>
        </transition>

        <notifications style="top:5%;" width="35%" group="error" position="top center" />
        <notifications style="top:5%;" width="35%" group="success" position="top center" />
    </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import AlertService from '@/services/alertService';
import helper from "@/services/helper";

export default {
    components: { PageTitle, LoadingSpinner },
    name: "BoosterPlansList",

    data() {
        return {
            plans: [],
            showModal: false,
            isEditing: false,
            editId: null,
            searchTerm: "",
            loader: false,
            form: {
                name: "",
                description: "",
                duration_days: null,
                price: null,
                sort_order: 0,
                is_active: true
            }
        };
    },

    computed: {
        filteredPlans() {
            if (!this.searchTerm) return this.plans;

            const term=this.searchTerm.toLowerCase();
            return this.plans.filter(plan =>
                plan.name.toLowerCase().includes(term)||
                plan.description.toLowerCase().includes(term)||
                plan.duration_days.toString().includes(term)
            );
        },

        isFormValid() {
            return this.form.name&&
                this.form.description&&
                this.form.duration_days>0&&
                this.form.price>=0;
        }
    },

    mounted() {
        this.loadPlans();
    },

    methods: {
        clearSearch() {
            this.searchTerm="";
        },
        formatPrice: helper.formatPrice,
        async loadPlans() {
            try {
                this.loader=true;
                const res=await this.$axios.get("/admin/booster-plans");
                this.plans=Array.isArray(res.data.data)
                    ? res.data.data
                    :Object.values(res.data.data);
                this.loader=false;

            } catch (err) {
                console.error("Error loading booster plans:", err);
                this.$notify({
                    group: "error",
                    title: this.$t('sentence.errorLoading'),
                    type: "error",
                });
            }
        },

        openCreateModal() {
            this.isEditing=false;
            this.showModal=true;
            this.form={
                name: "",
                description: "",
                duration_days: null,
                price: null,
                sort_order: 0,
                is_active: true
            };
        },

        closeModal() {
            this.showModal=false;
        },

        async save() {
            if (!this.isFormValid) return;

            try {
                if (this.isEditing) {
                    const res=await this.$axios.put(`/admin/booster-plans/${this.editId}`, this.form);
                    this.$notify({
                        group: "success",
                        title: res.data.message||this.$t('sentence.updateSuccess'),
                        type: "success",
                    });
                } else {
                    const res=await this.$axios.post("/admin/booster-plans", this.form);
                    this.$notify({
                        group: "success",
                        title: res.data.message||this.$t('sentence.createSuccess'),
                        type: "success",
                    });
                }
                this.closeModal();
                this.loadPlans();
            } catch (err) {
                console.error("Error saving booster plan:", err);
                this.$notify({
                    group: "error",
                    title: err.response?.data?.message||this.$t('sentence.errorSaving'),
                    type: "error",
                });
            }
        },

        async edit(id) {
            this.isEditing=true;
            this.editId=id;

            try {
                const res=await this.$axios.get(`/admin/booster-plans/${id}`);
                this.form={ ...res.data };
                this.showModal=true;
            } catch (err) {
                console.error("Error loading booster plan:", err);
                this.$notify({
                    group: "error",
                    title: err.response?.data?.message||this.$t('sentence.errorLoading'),
                    type: "error",
                });
            }
        },

        async remove(id) {
            const confirmed=await AlertService.confirm({
                title: this.$t('sentence.confirmDelete')||"Are you sure?",
                confirmButtonText: this.$t('action.delete')||"Delete",
                cancelButtonText: this.$t('action.cancel')||"Cancel",
                icon: "warning"
            });

            if (!confirmed) return;

            try {
                const res=await this.$axios.delete(`/admin/booster-plans/${id}`);
                this.$notify({
                    group: "success",
                    title: res.data.message||this.$t('sentence.deleteSuccess'),
                    type: "success",
                });
                this.loadPlans();
            } catch (err) {
                console.error("Error deleting booster plan:", err);
                this.$notify({
                    group: "error",
                    title: err.response?.data?.message||this.$t('sentence.errorDeleting'),
                    type: "error",
                });
            }
        }
    }
};
</script>
<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>

/* Table action buttons */
.admin-action-btn {
    width: 32px;
    height: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.font-weight-semibold {
    font-weight: 600;
}

.gap-2 {
    gap: 0.5rem;
}

/* Form styles for drawer */
.form-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

.toggle-container {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}

.toggle-switch {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 26px;
}

.toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--color-c);
    transition: 0.3s;
    border-radius: 26px;
}

.toggle-slider:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
}

.toggle-switch input:checked+.toggle-slider {
    background-color: var(--primary-color, var(--color-nz-200));
}

.toggle-switch input:checked+.toggle-slider:before {
    transform: translateX(24px);
}

.toggle-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--gray-700);
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .form-row {
        grid-template-columns: 1fr;
    }
}
</style>